<template>
  <div class="gd-section">
    <div class="gd-section-head">
      <span class="n">{{ section }}</span>
      <span class="t">Atendimento</span>
      <span class="d">{{ headHint }}</span>
    </div>
    <div class="gd-section-body">
      <div class="columns" v-if="readonly">
        <div class="column col-4 col-sm-6" v-if="type === 'sadt'">
          <guide-read-field label="32 · Tipo do atendimento" :value="name(serviceTypes, 'type')"/>
        </div>
        <div class="column col-4 col-sm-6">
          <guide-read-field :label="labels.accidentIndication"
                            :value="name(accidentIndications, 'accidentIndication')"/>
        </div>
        <div class="column col-4 col-sm-6" v-if="showConsultationType">
          <guide-read-field :label="labels.consultationType"
                            :value="name(consultationTypes, 'consultationType')"/>
        </div>
        <div class="column col-4 col-sm-6">
          <guide-read-field :label="labels.serviceRegime"
                            :value="name(serviceRegimes, 'serviceRegime')"/>
        </div>
        <div class="column col-4 col-sm-6" v-if="type === 'sadt'">
          <guide-read-field label="35 · Motivo do encerramento"
                            :value="name(closureReasons, 'closureReason')"/>
        </div>
        <div class="column col-4 col-sm-6">
          <guide-read-field :label="labels.specialCoverage"
                            :value="name(specialCoverages, 'specialCoverage')"/>
        </div>
        <div class="column col-4 col-sm-6">
          <guide-read-field :label="labels.occupationalHealth"
                            :value="name(occupationalHealth, 'occupationalHealth')"/>
        </div>
      </div>
      <div class="columns" v-else>
        <div class="column col-4 col-md-6 col-sm-12 form-group" v-if="type === 'sadt'"
             :class="{'has-error': err('service.type')}">
          <label class="form-label">32 · Tipo do atendimento</label>
          <select class="form-select" v-model="form.service.type"
                  :id="fieldId('service.type')" @blur="touch('service.type')">
            <option value="">[Selecione]</option>
            <option v-for="(text, value) in serviceTypes" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
          <div class="form-input-hint" v-if="err('service.type')">{{ hint('service.type') }}</div>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group"
             :class="{'has-error': err('service.accidentIndication')}">
          <label class="form-label">{{ labels.accidentIndication }}</label>
          <select class="form-select" v-model="form.service.accidentIndication"
                  :id="fieldId('service.accidentIndication')"
                  @blur="touch('service.accidentIndication')">
            <option value="">[Selecione]</option>
            <option v-for="(text, value) in accidentIndications" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
          <div class="form-input-hint" v-if="err('service.accidentIndication')">
            {{ hint('service.accidentIndication') }}
          </div>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group" v-if="showConsultationType"
             :class="{'has-error': err('service.consultationType')}">
          <label class="form-label">{{ labels.consultationType }}</label>
          <select class="form-select" v-model="form.service.consultationType"
                  :id="fieldId('service.consultationType')"
                  @blur="touch('service.consultationType')"
                  @change="$emit('consultation-type')">
            <option value="">[Selecione]</option>
            <option v-for="(text, value) in consultationTypes" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
          <div class="form-input-hint" v-if="err('service.consultationType')">
            {{ hint('service.consultationType') }}
          </div>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group"
             :class="{'has-error': err('service.serviceRegime')}">
          <label class="form-label">{{ labels.serviceRegimeLong }}</label>
          <select class="form-select" v-model="form.service.serviceRegime"
                  :id="fieldId('service.serviceRegime')" @blur="touch('service.serviceRegime')">
            <option value="">[Selecione]</option>
            <option v-for="(text, value) in serviceRegimes" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
          <div class="form-input-hint" v-if="err('service.serviceRegime')">
            {{ hint('service.serviceRegime') }}
          </div>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group" v-if="type === 'sadt'">
          <label class="form-label">35 · Motivo do encerramento <small>opcional</small></label>
          <select class="form-select" v-model="form.service.closureReason">
            <option value="">—</option>
            <option v-for="(text, value) in closureReasons" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group">
          <label class="form-label">{{ labels.specialCoverage }} <small>opcional</small></label>
          <select class="form-select" v-model="form.service.specialCoverage">
            <option value="">—</option>
            <option v-for="(text, value) in specialCoverages" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
        </div>
        <div class="column col-4 col-md-6 col-sm-12 form-group">
          <label class="form-label">{{ labels.occupationalHealth }} <small>opcional</small></label>
          <select class="form-select" v-model="form.service.occupationalHealth">
            <option value="">—</option>
            <option v-for="(text, value) in occupationalHealth" :value="value" :key="value">
              {{ text }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { serviceTypes } from '@/data/service-types';
import { accidentIndications } from '@/data/accident-indications';
import { consultationTypes } from '@/data/consultation-types';
import { serviceRegimes } from '@/data/service-regimes';
import { closureReasons } from '@/data/closure-reasons';
import { specialCoverages } from '@/data/special-coverages';
import { occupationalHealth } from '@/data/occupational-health';
import fieldMixin from '../fieldMixin';
import GuideReadField from './GuideReadField.vue';

export default {
  mixins: [fieldMixin],
  components: {
    GuideReadField,
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    hasConsultationItem: {
      type: Boolean,
      default: false,
    },
    section: {
      type: String,
      default: '§5',
    },
  },
  data() {
    return {
      serviceTypes,
      accidentIndications,
      consultationTypes,
      serviceRegimes,
      closureReasons,
      specialCoverages,
      occupationalHealth,
    };
  },
  computed: {
    showConsultationType() {
      return this.type === 'consultation' || this.hasConsultationItem;
    },
    headHint() {
      if (this.type === 'sadt') return 'Campos 32–35, 90–92';
      if (this.type === 'consultation') return 'Campos 17, 19 e 27–29 da guia de consulta';
      return 'Campos 33, 34, 90–92';
    },
    labels() {
      if (this.type === 'consultation') {
        return {
          accidentIndication: '17 · Indicação de acidente',
          consultationType: '19 · Tipo de consulta',
          serviceRegime: '28 · Regime',
          serviceRegimeLong: '28 · Regime de atendimento',
          specialCoverage: '27 · Cobertura especial',
          occupationalHealth: '29 · Saúde ocupacional',
        };
      }
      return {
        accidentIndication: '33 · Indicação de acidente',
        consultationType: '34 · Tipo de consulta',
        serviceRegime: '91 · Regime',
        serviceRegimeLong: '91 · Regime de atendimento',
        specialCoverage: '90 · Cobertura especial',
        occupationalHealth: '92 · Saúde ocupacional',
      };
    },
  },
  methods: {
    name(dictionary, key) {
      const value = this.form.service[key];
      return value ? dictionary[value] || value : '';
    },
  },
};
</script>
